import { useCallback, useMemo, useState } from 'react';
import type { DripCampaignRow, DripCampaignStatus } from '../types';
import { DripSection } from './DripSection';
import { SITE_FORM_CONTROL } from '../../../constants/siteUi';
import { useDripToast } from './DripToastProvider';

const PAGE = 8;

const STATUS_STYLES: Record<DripCampaignStatus, string> = {
  draft: 'bg-white/10 text-white/85 ring-white/15',
  active: 'bg-emerald-500/15 text-emerald-200 ring-emerald-400/30',
  paused: 'bg-amber-500/15 text-amber-100 ring-amber-400/30',
  completed: 'bg-white/10 text-[var(--home-muted-on-dark)] ring-white/10',
  archived: 'bg-white/5 text-[var(--home-muted-on-dark)] ring-white/8',
};

function cmp(a: string | number, b: string | number, dir: 'asc' | 'desc'): number {
  if (a < b) return dir === 'asc' ? -1 : 1;
  if (a > b) return dir === 'asc' ? 1 : -1;
  return 0;
}

export function DripCampaignsTable({ rows: initialRows }: { rows: DripCampaignRow[] }) {
  const toast = useDripToast();
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<DripCampaignStatus | 'all'>('all');
  const [sortKey, setSortKey] = useState<keyof DripCampaignRow>('updatedAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return initialRows.filter((r) => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (!q) return true;
      return (
        r.name.toLowerCase().includes(q) ||
        r.audience.toLowerCase().includes(q) ||
        r.currentStep.toLowerCase().includes(q)
      );
    });
  }, [initialRows, query, statusFilter]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    copy.sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      if (typeof va === 'number' && typeof vb === 'number') return cmp(va, vb, sortDir);
      return cmp(String(va), String(vb), sortDir);
    });
    return copy;
  }, [filtered, sortKey, sortDir]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE));
  const pageSafe = Math.min(page, pageCount - 1);
  const pageRows = sorted.slice(pageSafe * PAGE, pageSafe * PAGE + PAGE);

  const toggleSort = (k: keyof DripCampaignRow) => {
    if (sortKey === k) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(k);
      setSortDir('desc');
    }
  };

  const toggleOne = useCallback((id: string) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }, []);

  const toggleAllPage = useCallback(() => {
    const ids = pageRows.map((r) => r.id);
    const allOn = ids.every((id) => selected.has(id));
    setSelected((s) => {
      const n = new Set(s);
      if (allOn) ids.forEach((id) => n.delete(id));
      else ids.forEach((id) => n.add(id));
      return n;
    });
  }, [pageRows, selected]);

  const act = (label: string) => {
    toast(`${label} queued — wire to ESP API for execution.`);
  };

  return (
    <DripSection
      id="drip-campaigns-table"
      title="Campaigns"
      description="Search, filter, sort, and bulk-operate — actions enqueue idempotent jobs server-side when connected."
      action={
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={selected.size === 0}
            className="rounded-lg border border-white/15 bg-black/25 px-3 py-1.5 text-[0.65rem] font-semibold uppercase tracking-wide text-[var(--home-text-on-dark)] disabled:opacity-40"
            onClick={() => act(`Pause ${selected.size} campaign(s)`)}
          >
            Pause
          </button>
          <button
            type="button"
            disabled={selected.size === 0}
            className="rounded-lg border border-white/15 bg-black/25 px-3 py-1.5 text-[0.65rem] font-semibold uppercase tracking-wide text-[var(--home-text-on-dark)] disabled:opacity-40"
            onClick={() => act(`Resume ${selected.size} campaign(s)`)}
          >
            Resume
          </button>
          <button
            type="button"
            disabled={selected.size === 0}
            className="rounded-lg border border-rose-400/35 bg-rose-500/10 px-3 py-1.5 text-[0.65rem] font-semibold uppercase tracking-wide text-rose-100 disabled:opacity-40"
            onClick={() => act(`Delete ${selected.size} draft(s)`)}
          >
            Delete
          </button>
        </div>
      }
    >
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <label className="sr-only" htmlFor="drip-campaign-search">
          Search campaigns
        </label>
        <input
          id="drip-campaign-search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(0);
          }}
          placeholder="Search name, audience, step…"
          className={`w-full max-w-md ${SITE_FORM_CONTROL}`}
        />
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor="drip-status-filter" className="sr-only">
            Filter by status
          </label>
          <select
            id="drip-status-filter"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as DripCampaignStatus | 'all');
              setPage(0);
            }}
            className={SITE_FORM_CONTROL}
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="draft">Draft</option>
            <option value="completed">Completed</option>
            <option value="archived">Archived</option>
          </select>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl ring-1 ring-white/[0.06]">
        <table className="min-w-[960px] w-full border-collapse text-left text-sm">
          <thead className="border-b border-white/[0.08] bg-black/25">
            <tr className="font-display text-[0.58rem] uppercase tracking-[0.14em] text-[var(--home-muted-on-dark)]">
              <th className="w-10 px-3 py-2">
                <input
                  type="checkbox"
                  aria-label="Select all on this page"
                  checked={pageRows.length > 0 && pageRows.every((r) => selected.has(r.id))}
                  onChange={toggleAllPage}
                />
              </th>
              {(
                [
                  ['name', 'Campaign'],
                  ['status', 'Status'],
                  ['audience', 'Audience'],
                  ['currentStep', 'Step'],
                  ['sent', 'Sent'],
                  ['opens', 'Opens'],
                  ['ctrPct', 'CTR'],
                  ['replies', 'Replies'],
                  ['conversions', 'Conv.'],
                  ['revenueUsd', 'Revenue'],
                  ['updatedAt', 'Updated'],
                ] as const
              ).map(([key, label]) => (
                <th key={key} className="px-3 py-2">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 rounded-md px-1 py-0.5 text-left hover:bg-black/25"
                    onClick={() => toggleSort(key)}
                    aria-sort={sortKey === key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    {label}
                    {sortKey === key ? <span aria-hidden>{sortDir === 'asc' ? '↑' : '↓'}</span> : null}
                  </button>
                </th>
              ))}
              <th className="px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <td colSpan={13} className="px-4 py-10 text-center text-[var(--home-muted-on-dark)]">
                  No campaigns match this view.
                </td>
              </tr>
            ) : (
              pageRows.map((r) => (
                <tr key={r.id} className="border-t border-white/[0.05]">
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      aria-label={`Select ${r.name}`}
                      checked={selected.has(r.id)}
                      onChange={() => toggleOne(r.id)}
                    />
                  </td>
                  <td className="max-w-[14rem] px-3 py-2 font-medium text-[var(--home-text-on-dark)]">{r.name}</td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-flex rounded-md px-2 py-0.5 text-[0.58rem] font-bold uppercase tracking-wide ring-1 ${STATUS_STYLES[r.status]}`}
                    >
                      {r.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-[var(--home-muted-on-dark)]">{r.audience}</td>
                  <td className="px-3 py-2 text-xs text-[var(--home-muted-on-dark)]">{r.currentStep}</td>
                  <td className="px-3 py-2 tabular-nums">{r.sent.toLocaleString()}</td>
                  <td className="px-3 py-2 tabular-nums">{r.opens.toLocaleString()}</td>
                  <td className="px-3 py-2 tabular-nums">{r.ctrPct.toFixed(1)}%</td>
                  <td className="px-3 py-2 tabular-nums">{r.replies}</td>
                  <td className="px-3 py-2 tabular-nums">{r.conversions}</td>
                  <td className="px-3 py-2 tabular-nums">${r.revenueUsd.toLocaleString()}</td>
                  <td className="px-3 py-2 text-xs text-[var(--home-muted-on-dark)]">
                    {new Date(r.updatedAt).toLocaleString()}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      <button
                        type="button"
                        className="rounded-md border border-white/[0.08] bg-black/25 px-2 py-1 text-[0.6rem] font-semibold uppercase tracking-wide hover:bg-black/35"
                        onClick={() => act(`Edit ${r.id}`)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="rounded-md border border-white/[0.08] bg-black/25 px-2 py-1 text-[0.6rem] font-semibold uppercase tracking-wide hover:bg-black/35"
                        onClick={() => act(`Pause ${r.id}`)}
                      >
                        Pause
                      </button>
                      <button
                        type="button"
                        className="rounded-md border border-white/[0.08] bg-black/25 px-2 py-1 text-[0.6rem] font-semibold uppercase tracking-wide hover:bg-black/35"
                        onClick={() => act(`Duplicate ${r.id}`)}
                      >
                        Dup
                      </button>
                      <button
                        type="button"
                        className="rounded-md bg-[var(--home-miami-blue)]/20 px-2 py-1 text-[0.6rem] font-semibold uppercase tracking-wide text-[var(--home-miami-blue)] hover:bg-[var(--home-miami-blue)]/30"
                        onClick={() => act(`Analytics ${r.id}`)}
                      >
                        Stats
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-xs text-[var(--home-muted-on-dark)]">
        <p>
          Page {pageSafe + 1} of {pageCount} · {sorted.length} campaigns
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            className="rounded-lg border border-white/12 px-3 py-1.5 text-[var(--home-text-on-dark)] disabled:opacity-40"
            disabled={pageSafe <= 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            Previous
          </button>
          <button
            type="button"
            className="rounded-lg border border-white/12 px-3 py-1.5 text-[var(--home-text-on-dark)] disabled:opacity-40"
            disabled={pageSafe >= pageCount - 1}
            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
          >
            Next
          </button>
        </div>
      </div>
    </DripSection>
  );
}
